import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'antd';
import { FORM_ERROR } from 'final-form';

import FieldWithValidation from '@/components/FieldWithValidation';

import { isEmail } from '@/utils';

import { logIn } from '@/store/user/thunkActions';

import type { ValidationErrorsObject } from '@/types/global';
import { useTypedDispatch } from '@/store';

const Login = () => {
  const [t] = useTranslation();
  const history = useNavigate();
  const dispatch = useTypedDispatch();

  const onSubmit = async (values: { email: string; password: string }) => {
    try {
      await dispatch(logIn(values));
      history('/');
    } catch (e: any) {
      return { [FORM_ERROR]: e.message };
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      render={({ handleSubmit, submitError, submitting }) => (
        <form className="auth-form auth-form--login" onSubmit={handleSubmit}>
          <div className="auth-form__title">{t('logIn')}</div>

          {submitError && <div className="auth-form__error">{submitError}</div>}

          <div className="auth-form__fields">
            <Field name="email">
              {({ input, meta }) => (
                <FieldWithValidation meta={meta} errorText={t(meta.error)}>
                  <Input {...input} type="email" placeholder="Email" />
                </FieldWithValidation>
              )}
            </Field>
            <Field name="password">
              {({ input, meta }) => (
                <FieldWithValidation meta={meta} errorText={t(meta.error)}>
                  <Input.Password {...input} placeholder={t('password')} />
                </FieldWithValidation>
              )}
            </Field>
          </div>

          <Link to="/forgot-password" className="auth-form__forgot">
            {t('forgotPassword')}?
          </Link>
          <Button className="auth-form__button" type="primary" htmlType="submit" loading={submitting}>
            {t('logInVerb')}
          </Button>
          <div className="auth-form__footer-text">
            {t('dontHaveAccount')} <Link to="/signup">{t('signUpVerb')}</Link>
          </div>
        </form>
      )}
    />
  );
};

type FormState = {
  email: string;
  password: string;
};

const validate = (values: Partial<FormState>) => {
  const errors: ValidationErrorsObject<FormState> = {};

  if (!values.email) {
    errors.email = 'required';
  }
  if (values.email && !isEmail(values.email)) {
    errors.email = 'invalidEmail';
  }
  if (!values.password || values.password.trim().length < 3) {
    errors.password = 'minLength';
  }

  return errors;
};

export default Login;
